import React from 'react';

import { HorizontalSpace } from '@components/HorizontalSpace';
import { QueryContainer } from '@components/QueryContainer';
import { VerticalSpace } from '@components/VerticalSpace';
import { warehouseColorNames, warehouseColors } from '@data/warehouse';
import { useAgency } from '@hooks/queries/dashboard/useAgency';
import { useWarehouses } from '@hooks/queries/dashboard/useWarehouses';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';

const strings = createStrings({
  ja: {
    title: (agencyName: string) => `${agencyName}倉庫一覧`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  selectorContainer: {
    ...common.flexColumn,
    ...common.middleText,
    flex: 1,
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: '1rem',
  },
  selectorDisplay: {
    ...common.flexColumn,
    ...common.fontWeight500,
    '& > div:hover': {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
  },
  selectorItem: {
    ...common.flexInline,
    ...common.baseButton,
    padding: '0.5rem 0',
    borderRadius: '0.4rem',
  },
  goBackButton: {
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderLeft: `12px solid ${theme.palette.primary.main}`,
    ...common.baseButton,
  },
  indicator: {
    width: 10,
    height: '100%',
    '&.color-pink': { background: warehouseColors.pink },
    '&.color-blue': { background: warehouseColors.blue },
    '&.color-green': { background: warehouseColors.green },
    '&.color-red': { background: warehouseColors.red },
    '&.color-brown': { background: warehouseColors.brown },
    '&.color-yellow': { background: warehouseColors.yellow },
  },
}));

interface WarehouseSelectorProps {
  agencyId?: number;
  warehouseIdList?: number[];
  onClick: (warehouseId: number) => void;
  onGoBack: () => void;
  isEditing: boolean;
}

export const WarehouseSelector: React.FC<WarehouseSelectorProps> = (props) => {
  const { agencyId, onClick, onGoBack, isEditing, warehouseIdList } = props;

  const warehouses = useWarehouses({ agency_id: agencyId }, { enabled: !!agencyId });
  const agency = useAgency(agencyId!, { enabled: !!agencyId });

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.selectorContainer}>
      <QueryContainer queries={[warehouses, agency]}>
        <div className={clsx(classes.flexInline, classes.justifyBetween)}>
          <div>{strings.title(agency.data?.name_en || '')}</div>
          {isEditing && <div className={classes.goBackButton} onClick={onGoBack}></div>}
        </div>
        <VerticalSpace height={20} />
        <div className={classes.selectorDisplay}>
          {warehouseIdList?.map((warehouseId, index) => (
            <div
              key={warehouseId}
              onClick={() => onClick(warehouseId)}
              className={classes.selectorItem}
            >
              <div className={clsx(classes.indicator, `color-${warehouseColorNames[index]}`)} />
              <HorizontalSpace width={10} />
              {warehouses?.data?.find((warehouse) => warehouse.id === warehouseId)?.name}
            </div>
          ))}
        </div>
      </QueryContainer>
    </div>
  );
};
